import { PageHeader } from '@metorial/layout';
import { useOAuthScopes, useUploadFile } from '@metorial/state';
import {
  Avatar,
  Button,
  Copy,
  Dialog,
  showModal,
  Spacer,
  Tabs,
  Text,
  theme,
  Title,
  toast
} from '@metorial/ui';
import { Box } from '@metorial/ui-product';
import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { useShellScope } from '../../../shell';

export type { ShellPortal as ShellPortalLike } from '../../../shell';

// Shared building blocks for the BlackOS settings pages (cloud-spec §7).

/** Org, project and instance for the current settings URL (ids and slugs from boot). */
export let useSettingsScope = () => {
  let scope = useShellScope();
  let org = scope.organization;

  return {
    ...scope,
    organizationId: org?.id ?? null,
    projectId: scope.project?.id ?? null,
    instanceId: scope.instance?.id ?? null,
    isAdmin: (org as any)?.member?.role == 'admin'
  };
};

export let Stack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${p => p.$gap ?? 20}px;
`;

export let Row = styled.div<{ $gap?: number }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${p => p.$gap ?? 10}px;
`;

export let Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/** A settings card: title + description header and a white body (cloud "card" with Save). */
export let SettingsCard = ({
  title,
  description,
  actions,
  children,
  footer,
  noPadding
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  noPadding?: boolean;
}) => (
  <Box title={title} description={description} rightActions={actions} noPadding={noPadding}>
    {children}
    {footer && (
      <>
        {children && <Spacer size={16} />}
        <Row>{footer}</Row>
      </>
    )}
  </Box>
);

export let SaveButton = ({
  loading,
  success,
  disabled,
  children = 'Save',
  type = 'submit',
  onClick
}: {
  loading?: boolean;
  success?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  type?: 'submit' | 'button';
  onClick?: () => void;
}) => (
  <Button
    size="2"
    type={type}
    loading={loading}
    success={success}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </Button>
);

/** A small outline button that navigates (row actions like `Open`). */
export let LinkButton = ({
  to,
  children,
  variant = 'outline',
  size = '1'
}: {
  to: string;
  children: React.ReactNode;
  variant?: 'outline' | 'soft' | 'solid';
  size?: '1' | '2';
}) => (
  <Link to={to} onClick={e => e.stopPropagation()}>
    <Button size={size} variant={variant} as="span">
      {children}
    </Button>
  </Link>
);

export let EmptyText = ({ children }: { children: React.ReactNode }) => (
  <Text size="2" color="gray600" style={{ padding: '6px 0' }}>
    {children}
  </Text>
);

export let Muted = ({ children }: { children: React.ReactNode }) => (
  <Text size="2" color="gray600" as="span">
    {children}
  </Text>
);

/** Avatar + name + email, used in member and actor columns. */
export let PersonCell = ({
  name,
  email,
  imageUrl,
  size = 32
}: {
  name: string;
  email?: string | null;
  imageUrl?: string | null;
  size?: number;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
    <Avatar size={size} entity={{ name, imageUrl: imageUrl ?? undefined }} />
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Title as="h3" size="2" weight="bold">
        {name}
      </Title>
      {email && (
        <Text size="1" color="gray600">
          {email}
        </Text>
      )}
    </div>
  </div>
);

let ChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

let ChipEl = styled.span<{ $tone: 'gray' | 'blue' | 'purple' | 'green' | 'orange' | 'red' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  ${p =>
    ({
      gray: `background: ${theme.colors.gray200}; color: ${theme.colors.gray800};`,
      blue: 'background: #eaf5ff; color: #0b68cb;',
      purple: 'background: #f3edff; color: #6e3fd1;',
      green: 'background: #e9f9ee; color: #18794e;',
      orange: 'background: #fff1e2; color: #a35200;',
      red: 'background: #ffefef; color: #c62a2f;'
    })[p.$tone]}
`;

export let Chip = ({
  children,
  tone = 'gray',
  title
}: {
  children: React.ReactNode;
  tone?: 'gray' | 'blue' | 'purple' | 'green' | 'orange' | 'red';
  title?: string;
}) => (
  <ChipEl $tone={tone} title={title}>
    {children}
  </ChipEl>
);

export let Chips = ({ children }: { children: React.ReactNode }) => (
  <ChipWrap>{children}</ChipWrap>
);

/** Detail page header: breadcrumb (`Members > Name`), H1, actions and in-page tabs. */
export let DetailHeader = ({
  parent,
  title,
  description,
  actions
}: {
  parent: { label: string; to: string };
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}) => (
  <PageHeader
    top={
      <Text size="2" weight="medium" color="gray600">
        <Link to={parent.to} style={{ color: 'inherit' }}>
          {parent.label}
        </Link>
        {'  ›  '}
        <span style={{ color: theme.colors.gray900 }}>{title}</span>
      </Text>
    }
    title={title}
    description={description}
    actions={actions}
  />
);

/** In-page tabs kept in `?tab=` so links and reloads land on the same tab. */
export let useTabParam = <T extends string>(tabs: readonly T[], fallback: T) => {
  let [search, setSearch] = useSearchParams();
  let raw = search.get('tab') as T | null;
  let current = raw && tabs.includes(raw) ? raw : fallback;

  let setTab = (tab: T) => {
    let next = new URLSearchParams(search);
    if (tab == fallback) next.delete('tab');
    else next.set('tab', tab);
    setSearch(next, { replace: true });
  };

  return [current, setTab] as const;
};

export let PageTabs = <T extends string>({
  tabs,
  current,
  onChange
}: {
  tabs: { id: T; label: string }[];
  current: T;
  onChange: (id: T) => void;
}) => (
  <Tabs
    current={current}
    tabs={tabs}
    action={id => onChange(id as T)}
    margin={{ top: 0, bottom: 16 }}
  />
);

// ---------------------------------------------------------------------------
// Scopes: the catalog of permission scopes (`organization.member:read`, ...).

export type ScopeInfo = {
  identifier: string;
  name: string;
  description: string;
  dependencies: string[];
};

export let scopeArea = (identifier: string) => {
  let [resource] = identifier.split(':');
  let [root] = resource!.split('.');
  if (root == 'user') return 'User';
  if (root == 'organization') return 'Organization';
  if (root == 'instance') return 'Instance';
  return 'Other';
};

export let useScopeCatalog = (organizationId: string | null | undefined) => {
  let scopes = useOAuthScopes(organizationId);
  let items = useMemo(
    () => ((scopes.data as any)?.permissions ?? []) as ScopeInfo[],
    [scopes.data]
  );
  let byId = useMemo(() => new Map(items.map(s => [s.identifier, s])), [items]);

  return { ...scopes, items, byId };
};

/**
 * "Upload Image" button: picks an image, uploads it to the instance's file
 * store with the given purpose and hands the file id to `onUploaded`.
 */
export let ImageUploadButton = ({
  instanceId,
  purpose,
  label = 'Upload Image',
  onUploaded,
  disabled
}: {
  instanceId: string | null | undefined;
  purpose: 'organization_image' | 'project_brand_image' | 'user_image';
  label?: string;
  onUploaded: (fileId: string) => Promise<unknown> | unknown;
  disabled?: boolean;
}) => {
  let input = React.useRef<HTMLInputElement>(null);
  let upload = useUploadFile();
  let [busy, setBusy] = React.useState(false);

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        style={{ display: 'none' }}
        onChange={async e => {
          let file = e.target.files?.[0];
          e.target.value = '';
          if (!file || !instanceId) return;
          if (file.size > 5 * 1024 * 1024) {
            toast.error('Images can be at most 5 MB.');
            return;
          }

          setBusy(true);
          try {
            let [res] = await upload.mutate({ instanceId, file, purpose, title: file.name });
            if (res) await onUploaded((res as any).id);
          } finally {
            setBusy(false);
          }
        }}
      />
      <Button
        size="2"
        variant="outline"
        type="button"
        loading={busy}
        disabled={disabled || !instanceId}
        onClick={() => input.current?.click()}
      >
        {label}
      </Button>
    </>
  );
};

/** Shows a freshly created secret once, with a copy field. */
export let showSecretModal = (opts: {
  title: string;
  description: string;
  secret: string;
  label?: string;
}) =>
  showModal(({ dialogProps, close }) => (
    <Dialog.Wrapper {...dialogProps} width={520}>
      <Dialog.Title>{opts.title}</Dialog.Title>
      <Dialog.Description>{opts.description}</Dialog.Description>
      <Copy label={opts.label ?? 'Client Secret'} value={opts.secret} />
      <Spacer size={15} />
      <Dialog.Actions>
        <Button size="2" type="button" onClick={close}>
          Done
        </Button>
      </Dialog.Actions>
    </Dialog.Wrapper>
  ));

export let formatDate = (date: Date | string | null | undefined) => {
  if (!date) return '-';
  let d = typeof date == 'string' ? new Date(date) : date;
  return d.toLocaleString();
};
